/* Generate a Studict-branded "I scored X/Y" PNG card using Canvas API.
 * Returns a Promise<Blob>.
 */

const SIZE = 1080;
const PALETTE = {
  bg: "#FDE047",
  ink: "#0F172A",
  accent: "#86EFAC",
  paper: "#FFFFFF",
  pink: "#F9A8D4",
};

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function nbCard(ctx, x, y, w, h, fill, shadow = 12, r = 22) {
  // shadow block (Neo-Brutalist offset)
  ctx.fillStyle = PALETTE.ink;
  roundedRect(ctx, x + shadow, y + shadow, w, h, r);
  ctx.fill();
  // body
  ctx.fillStyle = fill;
  roundedRect(ctx, x, y, w, h, r);
  ctx.fill();
  // border
  ctx.lineWidth = 6;
  ctx.strokeStyle = PALETTE.ink;
  roundedRect(ctx, x, y, w, h, r);
  ctx.stroke();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  let cy = y;
  for (let n = 0; n < words.length; n++) {
    const test = line + words[n] + " ";
    if (ctx.measureText(test).width > maxWidth && n > 0) {
      ctx.fillText(line, x, cy);
      line = words[n] + " ";
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line, x, cy);
  return cy;
}

function drawStudictGlyph(ctx, cx, cy, scale) {
  // Mirrors the PWA icon: book + bookmark + glossary lines.
  const bookW = 88 * scale;
  const bookH = 108 * scale;
  const stroke = 7 * scale;
  const x0 = cx - bookW / 2;
  const y0 = cy - bookH / 2;

  // book body — paper fill, ink stroke
  ctx.fillStyle = PALETTE.paper;
  ctx.strokeStyle = PALETTE.ink;
  ctx.lineWidth = stroke;
  ctx.fillRect(x0, y0, bookW, bookH);
  ctx.strokeRect(x0, y0, bookW, bookH);

  // spine
  const spineX = x0 + bookW * 0.32;
  ctx.beginPath();
  ctx.moveTo(spineX, y0 + stroke);
  ctx.lineTo(spineX, y0 + bookH - stroke);
  ctx.lineWidth = 4 * scale;
  ctx.stroke();

  // bookmark ribbon
  const ribW = 24 * scale;
  const ribH = 48 * scale;
  const rx = cx + bookW * 0.18;
  const ribTop = y0 - 4 * scale;
  ctx.fillStyle = PALETTE.ink;
  ctx.beginPath();
  ctx.moveTo(rx, ribTop);
  ctx.lineTo(rx + ribW, ribTop);
  ctx.lineTo(rx + ribW, ribTop + ribH);
  ctx.lineTo(rx + ribW / 2, ribTop + ribH - 14 * scale);
  ctx.lineTo(rx, ribTop + ribH);
  ctx.closePath();
  ctx.fill();

  // glossary lines
  const lineX = spineX + 10 * scale;
  const lineY = y0 + 30 * scale;
  for (let i = 0; i < 3; i++) {
    ctx.fillStyle = PALETTE.ink;
    ctx.fillRect(lineX, lineY + i * 18 * scale, (bookW - 50 * scale) - i * 10 * scale, 5 * scale);
  }
}

export async function generateScoreCardBlob({ score, total, topic, weakWord, name }) {
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  // background
  ctx.fillStyle = PALETTE.bg;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // grid dots accent
  ctx.fillStyle = "#0F172A18";
  for (let gx = 40; gx < SIZE; gx += 60) {
    for (let gy = 40; gy < SIZE; gy += 60) {
      ctx.beginPath();
      ctx.arc(gx, gy, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // top brand strip — logo glyph + wordmark
  drawStudictGlyph(ctx, 95, 100, 1.0);
  ctx.fillStyle = PALETTE.ink;
  ctx.font = "bold 36px 'Outfit', system-ui, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText("STUDICT", 160, 60);
  ctx.font = "500 26px 'Work Sans', system-ui, sans-serif";
  ctx.fillStyle = "#334155";
  ctx.fillText("Smart student dictionary", 160, 110);

  // score chip
  nbCard(ctx, 700, 60, 320, 110, PALETTE.paper, 8, 16);
  ctx.font = "bold 22px 'Outfit', system-ui, sans-serif";
  ctx.fillStyle = PALETTE.ink;
  ctx.fillText("MY SCORE", 730, 90);
  ctx.font = "900 64px 'Outfit', system-ui, sans-serif";
  ctx.fillText(`${score}/${total}`, 730, 110);

  // headline card
  nbCard(ctx, 60, 230, 960, 560, PALETTE.paper, 12, 28);

  ctx.fillStyle = PALETTE.ink;
  ctx.font = "900 110px 'Outfit', system-ui, sans-serif";
  ctx.fillText(`I just scored`, 100, 280);
  ctx.font = "900 240px 'Outfit', system-ui, sans-serif";
  ctx.fillText(`${score}/${total}`, 100, 360);

  // topic chip
  if (topic) {
    ctx.font = "bold 32px 'Outfit', system-ui, sans-serif";
    const topicText = `on ${topic}`;
    const tw = ctx.measureText(topicText).width + 60;
    nbCard(ctx, 100, 640, tw, 64, PALETTE.accent, 6, 32);
    ctx.fillStyle = PALETTE.ink;
    ctx.fillText(topicText, 130, 656);
  }

  // weak word
  if (weakWord) {
    ctx.font = "500 28px 'Work Sans', system-ui, sans-serif";
    ctx.fillStyle = "#475569";
    wrapText(ctx, `Need to revise: "${weakWord}"`, 100, 730, 860, 36);
  }

  // bottom CTA — now includes the URL so any share gets people to the site
  nbCard(ctx, 60, 850, 960, 170, PALETTE.pink, 10, 24);
  ctx.fillStyle = PALETTE.ink;
  ctx.font = "900 46px 'Outfit', system-ui, sans-serif";
  ctx.fillText("Build your own glossary", 100, 880);
  ctx.font = "bold 32px 'Outfit', system-ui, sans-serif";
  ctx.fillText("studictsmartdictionary.com", 100, 950);

  // signature
  if (name) {
    ctx.font = "bold 28px 'Outfit', system-ui, sans-serif";
    ctx.fillStyle = PALETTE.ink;
    const sig = `— ${name}`;
    const w = ctx.measureText(sig).width;
    ctx.fillText(sig, SIZE - w - 100, 950);
  }

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}

export async function shareOrDownload(blob, filename, shareText) {
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "My Studict score", text: shareText });
      return "shared";
    } catch (err) {
      // User cancelled or share failed; fall through to download
      if (err && err.name !== "AbortError") {
        console.warn("share failed, falling back to download", err);
      }
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
  return "downloaded";
}
